import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { Button, Dropdown, Table, Typography } from '@leapeasy/ui-kit';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { getDemoData } from 'utils/helpers';
import { GlobalUXState } from 'components/GlobalUXState';
import { USER_ROLE } from 'data/constants/common_constants';
import { PolicyCancelLogDateRangeFilterFields } from 'components/PolicyCancelLog/PolicyCancelLogDateRangeFilterFields';
import {
  buildCancelLogTableRow,
  cancelLogRowToTableData,
  POLICY_CANCEL_LOG_ROWS_PER_PAGE_OPTIONS,
} from 'utils/policyCancelLog';

const RowText = styled(Typography)`
  word-break: normal;
  overflow-wrap: break-word;
`;

const ROLE_FILTER_OPTIONS = Object.entries(USER_ROLE).map(([value, text]) => ({
  text,
  value,
}));

/** Default column sizing — persisted widths use columnWidthStorageKey below. */
const CANCEL_LOG_COLUMNS = [
  {
    name: 'Rider ID',
    options: {
      sort: true,
      minWidth: 150,
      flex: '150px 0 0',
      shrink: false,
    },
    key: 'rider_id',
  },
  {
    name: 'Resident 1 Name',
    options: {
      sort: true,
      minWidth: 180,
      flex: '180px 1 1',
      flexGrow: 1,
    },
    key: 'tenant_name',
  },
  {
    name: 'Property Name',
    options: {
      sort: true,
      minWidth: 220,
      flex: '220px 1 1',
      flexGrow: 1,
    },
    key: 'building_name',
  },
  {
    name: 'Cancelled By',
    options: {
      sort: true,
      minWidth: 220,
      flex: '220px 1 1',
      flexGrow: 1,
    },
    key: 'cancelled_by',
  },
  {
    name: 'User Role',
    options: {
      sort: true,
      minWidth: 130,
      flex: '130px 0 0',
      shrink: false,
    },
    key: 'user_role',
  },
  {
    name: 'Cancellation Date',
    options: {
      sort: true,
      minWidth: 180,
      flex: '180px 0 0',
      shrink: false,
    },
    key: 'cancellation_date',
  },
  {
    name: '',
    options: {
      sort: false,
      shrink: false,
      allowOverflow: true,
      minWidth: 120,
      excludeFromExport: true,
    },
    key: 'actions',
  },
];

export function PolicyCancelLogTable({
  cancelLogs,
  loading,
  onViewApplication,
  filter,
  onChangeFilter,
  sortOptions,
  onChangeSort,
  pagination,
  onChangePagination,
  selectedRolesDraft,
  onSelectedRolesDraftChange,
  selectedCancellationDateDraft,
  onSelectedCancellationDateDraftChange,
  onApplyFilters,
  onResetFilters,
  dropFilterActiveCount = 0,
  hasAppliedFilters = false,
}) {
  const isDemo = useSelector((state) => state.getIn(['ui', 'demo']));

  const structuredRows = useMemo(
    () => cancelLogs.map((log, index) => buildCancelLogTableRow(log, index, isDemo)),
    [cancelLogs, isDemo],
  );

  const tableData = useMemo(
    () => structuredRows.map((row) => cancelLogRowToTableData(row, isDemo, getDemoData)),
    [structuredRows, isDemo],
  );

  const handleView = (entryIndex) => {
    const row = structuredRows[entryIndex];
    if (row?.application) {
      onViewApplication(row.application);
    }
  };

  const columns = useMemo(
    () =>
      CANCEL_LOG_COLUMNS.map((column) => {
        if (column.key === 'actions') {
          return {
            ...column,
            options: {
              ...column.options,
              customBodyRenderer: (entryIndex) => (
                <Box display="flex" justifyContent="flex-start" width="max-content">
                  <Button size="small" onClick={() => handleView(entryIndex)}>
                    View Details
                  </Button>
                </Box>
              ),
            },
          };
        }

        const isClickable = column.key === 'rider_id' || column.key === 'tenant_name';
        return {
          ...column,
          options: {
            ...column.options,
            customBodyRenderer: (value, tableMeta) => (
              <RowText
                align="left"
                variant="body2"
                onDoubleClick={isClickable ? () => handleView(tableMeta.rowData[6]) : undefined}
                title={isClickable ? 'Double-click to view application' : undefined}
                style={isClickable ? { cursor: 'pointer' } : undefined}
              >
                {value}
              </RowText>
            ),
          },
        };
      }),
    [structuredRows],
  );

  const totalItems = Number(pagination?.totalItems) || 0;

  return (
    <Table
      showEmptyState
      emptyContent={
        loading ? null : (
          <GlobalUXState
            variant={hasAppliedFilters ? 'noResults' : 'noData'}
            compact
            description={
              hasAppliedFilters
                ? 'No cancellation log entries match your search or filters.'
                : 'No policy cancellation activities to display.'
            }
          />
        )
      }
      data={tableData}
      filter={filter}
      onChangeFilter={onChangeFilter}
      dropFilter={{
        component: (
          <FilterPanel>
            <PolicyCancelLogDateRangeFilterFields
              value={selectedCancellationDateDraft}
              onChange={onSelectedCancellationDateDraftChange}
            />
            <Dropdown
              onChange={(e) => onSelectedRolesDraftChange(e.target.value)}
              options={ROLE_FILTER_OPTIONS}
              value={selectedRolesDraft}
              multiselect
              showSearch={false}
              placeholder=""
              label="User Role"
              style={{ width: '100%' }}
            />
          </FilterPanel>
        ),
        resetFunc: onResetFilters,
        applyFunc: onApplyFilters,
      }}
      dropFilterActiveCount={dropFilterActiveCount}
      sortOptions={sortOptions}
      onChangeSort={onChangeSort}
      onChangeRowsPerPage={(rowsPerPage) =>
        onChangePagination((prev) => ({
          ...prev,
          rowsPerPage,
          pageNumber: 0,
        }))
      }
      rowsPerPageOptions={POLICY_CANCEL_LOG_ROWS_PER_PAGE_OPTIONS}
      pagination={pagination}
      style={{ width: '100%' }}
      title="Policy Cancel Log"
      totalResult={totalItems}
      leastColumnCount={1}
      autoTooltipForDefaultCells
      columnWidthStorageKey="policy-cancel-log-v2"
      columns={columns}
    />
  );
}

const FilterPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;
